
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Lead } from "@/api/entities";
import { User } from "@/api/entities";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus, Filter, SlidersHorizontal, Download, User as UserIcon, RefreshCw, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAppUsers } from "@/hooks/useAppUsers";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CalendarIcon, X } from "lucide-react";

import LeadForm from "../components/leads/LeadForm";
import LeadList from "../components/leads/LeadList";
import LeadListView from "../components/leads/LeadListView";
import LeadTable from "../components/leads/LeadTable";
import LeadKanban from "../components/leads/LeadKanban";

import { ViewSwitcher } from "@/components/common/ViewSwitcher";
import { PrimaryButton } from "@/components/common/PrimaryButton";
import BulkActionBar from "@/components/common/BulkActionBar";
import { useToast } from "@/components/ui/use-toast";
import { ActiveFilters } from "@/components/common/ActiveFilters";
import FallingPetals from "@/components/animations/FallingPetals";
import HeaderFlowers from "@/components/animations/HeaderFlowers";
import { useUserUIPreferences } from "@/hooks/useUserUIPreferences";
import { useFixLeadMarket } from "@/hooks/useFixLeadMarket";
import { useBulkAssignLeads } from "@/hooks/useBulkAssignLeads";
import { useLeadAssignment } from "@/hooks/useLeadAssignment";

export default function Leads() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { users: allUsers, emailGroupMap } = useAppUsers(); // Use deduplicated users from hook
  const { mutate: fixMarket, isPending: isFixingMarket } = useFixLeadMarket();
  const { mutate: bulkAssignLeads, isPending: isAssigningLeads } = useBulkAssignLeads();
  const { getAssignedUserForLead } = useLeadAssignment();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [viewMode, setViewMode] = useState("kanban"); // Changed default viewMode from "grid" to "kanban"
  const [showFilters, setShowFilters] = useState(false);
  const [user, setUser] = useState(null); // Added user state
  const users = allUsers || []; // Use deduplicated users
  
  // UI Preferences hook
  const { shouldShowPetals, shouldShowFlowers, isLoading: prefsLoading } = useUserUIPreferences();
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    contact: true,
    company: true,
    status: true,
    source: true,
    assignedTo: true,
    createdBy: true,
    createdDate: true
  });
  
  // Filter states
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState(null);
  const [sortBy, setSortBy] = useState("created_date_desc");
  const [kanbanColumnsFilter, setKanbanColumnsFilter] = useState("all");
  const [marketFilter, setMarketFilter] = useState("all");
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  
  // Selection state
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);

  const getAssignedUserName = useCallback((assignedTo) => {
    if (!assignedTo || !users || users.length === 0) return null;
    const u = users.find((u) => u.id === assignedTo || u.email === assignedTo);
    if (!u) return null;
    return u.display_name || [u.first_name, u.last_name].filter(Boolean).join(" ");
  }, [users]);

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      // Load from Supabase leads table (simple select; we'll enrich names client-side)
      const { data, error } = await supabase
        .from('leads')
        .select(`
          id,
          first_name,
          last_name,
          primary_phone,
          primary_email,
          lifecycle_stage,
          created_at,
          raw_data,
          assigned_to,
          market
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const LIFECYCLE_TO_STATUS = {
        new: 'new',
        engaged: 'contacted',
        verified_person: 'qualified',
        disqualified: 'not_qualified',
        duplicate: 'not_qualified',
        lost: 'not_qualified',
        qualified: 'qualified',
        not_qualified: 'not_qualified',
        contacted: 'contacted',
        qualifying: 'qualifying',
      };

      const mapped = (data || []).map((row) => {
        const formSubmittedAt = row.raw_data?.created_time || row.created_at;
        return {
          id: row.id,
          first_name: row.first_name || 'Lead',
          last_name: row.last_name || '',
          phone: row.primary_phone || null,
          email: row.primary_email || null,
          status: LIFECYCLE_TO_STATUS[row.lifecycle_stage] || 'new',
          created_date: formSubmittedAt,
          assigned_to: row.assigned_to,
          market: row.market ? row.market.toUpperCase() : null,
        };
      });

      setLeads(mapped);
      setFilteredLeads(mapped);
    } catch (e) {
      console.error('Failed to load leads', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser(); // Load user data on mount
    loadLeads();
  }, []);

  // Auto-open form when navigating from WhatsApp with action=create
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    const phone = urlParams.get('phone');
    
    if (action === 'create' && phone) {
      setShowForm(true);
      // Clear the URL params to avoid reopening on refresh
      window.history.replaceState({}, '', '/Leads');
    }
  }, []);

  // Load saved filter preferences
  useEffect(() => {
    if (user?.metadata?.leads?.filters) {
      const savedFilters = user.metadata.leads.filters;
      if (savedFilters.viewMode) setViewMode(savedFilters.viewMode);
      if (savedFilters.assignedFilter) setAssignedFilter(savedFilters.assignedFilter);
      if (savedFilters.dateFilter) setDateFilter(savedFilters.dateFilter);
      if (savedFilters.sortBy) setSortBy(savedFilters.sortBy);
      if (savedFilters.kanbanColumnsFilter) setKanbanColumnsFilter(savedFilters.kanbanColumnsFilter);
      if (savedFilters.marketFilter) setMarketFilter(savedFilters.marketFilter);
      if (savedFilters.visibleColumns) setVisibleColumns(savedFilters.visibleColumns);
      if (savedFilters.searchQuery) setSearchQuery(savedFilters.searchQuery);
      setFiltersLoaded(true);
    } else if (user) {
      setFiltersLoaded(true);
    }
  }, [user]);

  // Save filter preferences with debounce
  useEffect(() => {
    if (!user?.id || !filtersLoaded) return;
    
    const timer = setTimeout(async () => {
      const updatedSettings = {
        ...user.metadata,
        leads: {
          ...user.metadata?.leads,
          filters: {
            viewMode,
            assignedFilter,
            dateFilter,
            sortBy,
            kanbanColumnsFilter,
            marketFilter,
            visibleColumns,
            searchQuery
          }
        }
      };
      
      try {
        await User.updateMyUserData({ metadata: updatedSettings });
        setUser(prev => ({ ...prev, metadata: updatedSettings }));
      } catch (error) {
        console.error("Error saving filter preferences:", error);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [viewMode, assignedFilter, dateFilter, sortBy, kanbanColumnsFilter, marketFilter, visibleColumns, searchQuery]);

  // Enrich leads with assigned user names using useMemo to prevent infinite loops
  const enrichedLeads = useMemo(() => {
    return leads.map(lead => ({
      ...lead,
      assigned_to_name: getAssignedUserName(lead.assigned_to)
    }));
  }, [leads, getAssignedUserName]);

  // Update filtered leads when search, filters or enriched leads change
  useEffect(() => {
    let filtered = [...enrichedLeads];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => {
        const fullName = `${lead.first_name} ${lead.last_name}`.toLowerCase();
        return (
          fullName.includes(query) ||
          lead.email?.toLowerCase().includes(query) ||
          lead.phone?.toLowerCase().includes(query) ||
          lead.company?.toLowerCase().includes(query)
        );
      });
    }

    // Apply assigned filter
    if (assignedFilter !== "all") {
      if (assignedFilter === "unassigned") {
        filtered = filtered.filter(lead => !lead.assigned_to);
      } else if (assignedFilter === "my_leads") {
        filtered = filtered.filter(lead => lead.assigned_to === user?.id);
      } else {
        // Use emailGroupMap to include all IDs from the same email group
        const idsToMatch = emailGroupMap?.get(assignedFilter) || [assignedFilter];
        filtered = filtered.filter(lead => 
          lead.assigned_to && idsToMatch.includes(lead.assigned_to)
        );
      }
    }

    // Apply date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.created_date);
        
        if (dateFilter === "today") {
          return leadDate >= today;
        } else if (dateFilter === "yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return leadDate >= yesterday && leadDate < today;
        } else if (dateFilter === "this_week") {
          const firstDayOfWeek = new Date(today);
          firstDayOfWeek.setDate(today.getDate() - today.getDay());
          return leadDate >= firstDayOfWeek;
        } else if (dateFilter === "this_month") {
          const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          return leadDate >= firstDayOfMonth;
        } else if (dateFilter === "custom" && customDateRange?.from) {
          const fromDate = new Date(customDateRange.from);
          const startOfDay = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
          
          if (customDateRange.to) {
            const toDate = new Date(customDateRange.to);
            const endOfDay = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59);
            return leadDate >= startOfDay && leadDate <= endOfDay;
          } else {
            const endOfDay = new Date(startOfDay);
            endOfDay.setDate(endOfDay.getDate() + 1);
            return leadDate >= startOfDay && leadDate < endOfDay;
          }
        }
        
        return true;
      });
    }

    // Apply market filter
    if (marketFilter !== "all") {
      filtered = filtered.filter(lead => {
        if (!lead.market) return false;
        return lead.market.toUpperCase() === marketFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "created_date_desc":
          return new Date(b.created_date) - new Date(a.created_date);
        case "created_date_asc":
          return new Date(a.created_date) - new Date(b.created_date);
        case "name_asc":
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        case "name_desc":
          return `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`);
        default:
          return 0;
      }
    });

    setFilteredLeads(filtered);
  }, [enrichedLeads, searchQuery, assignedFilter, dateFilter, customDateRange, marketFilter, sortBy]);


  const loadUser = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      
      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();
        
      if (error) throw error;
      setUser(data);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
  };

  const handleCreateLead = async (leadData) => {
    setIsProcessing(true);
    try {
      // Auto-assign using assignment rules
      let finalLeadData = { ...leadData };
      
      const assignedUserId = await getAssignedUserForLead({
        market: leadData.market,
        origem: leadData.source || 'website',
        source: leadData.source || 'website'
      });
      
      if (assignedUserId) {
        finalLeadData.assigned_to = assignedUserId;
        console.log('✅ Lead auto-assigned to user UUID:', assignedUserId);
      } else {
        console.log('⚠️ No auto-assignment performed');
      }

      // Mapear campos para o formato do banco (somente colunas válidas)
      const supabaseData = {
        first_name: finalLeadData.first_name,
        last_name: finalLeadData.last_name,
        primary_phone: finalLeadData.primary_phone,
        primary_email: finalLeadData.primary_email,
        lifecycle_stage: finalLeadData.lifecycle_stage || 'new',
        origem: finalLeadData.source || 'website',
        market: finalLeadData.market, // obrigatório segundo constraint; vem do formulário
        assigned_to: finalLeadData.assigned_to || null,
      };

      // Create the lead
      const { data: newLead, error: insertError } = await supabase
        .from('leads')
        .insert(supabaseData)
        .select()
        .single();

      if (insertError) throw insertError;

      // Create record in leads_marketing_data to enable auto-assignment
      const { error: marketingError } = await supabase
        .from('leads_marketing_data')
        .insert({
          lead_id: newLead.id,
          market: finalLeadData.market,
          source_platform: finalLeadData.source_platform || null,
          origem: finalLeadData.source || 'website',
        });

      if (marketingError) {
        console.error('Error creating marketing data:', marketingError);
        // Don't fail operation if marketing data is not created
      }

      setShowForm(false);
      await loadLeads();
      toast({ title: 'Lead created', description: 'The lead was created in the New column.' });
    } catch (error) {
      console.error("Error creating lead:", error);
      toast({
        title: "Error",
        description: error?.message || "Could not create lead.",
        variant: "destructive",
      });
    }
    setIsProcessing(false);
  };

  const handleViewLead = (leadId) => {
    navigate(createPageUrl("LeadDetail") + "?id=" + leadId);
  };

  const handleStatusChange = async (lead, newStatus, notConvertedReason = null) => {
    // Map UI status to DB lifecycle_stage values enforced by CHECK constraint
    const STATUS_TO_LIFECYCLE = {
      new: 'new',
      contacted: 'contacted',
      qualifying: 'qualifying',
      qualified: 'qualified',
      not_qualified: 'not_qualified',
    };
    const toLifecycle = (status) => STATUS_TO_LIFECYCLE[status] ?? 'new';

    try {
      // Se está movendo para qualified, converte para contato
      if (newStatus === 'qualified') {
        // Busca dados completos do lead
        const { data: fullLeadData, error: fetchError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', lead.id)
          .single();
        
        if (fetchError) throw fetchError;
        
        // Cria o contato
        const { data: newContact, error: contactError } = await supabase
          .from('contacts')
          .insert({
            first_name: fullLeadData.first_name,
            last_name: fullLeadData.last_name,
            preferred_name: fullLeadData.preferred_name,
            primary_email: fullLeadData.primary_email,
            primary_phone: fullLeadData.primary_phone,
            primary_phone_country_code: fullLeadData.primary_phone_country_code,
            gender: fullLeadData.gender,
            language: fullLeadData.language,
            date_of_birth: fullLeadData.date_of_birth,
            country_of_birth: fullLeadData.country_code,
            status: 'active',
          })
          .select()
          .single();
        
        if (contactError) throw contactError;
        
        // Atualiza o lead com contact_id e marca como convertido
        const { error: updateError } = await supabase
          .from('leads')
          .update({ 
            lifecycle_stage: toLifecycle(newStatus),
            contact_id: newContact.id,
            converted_at: new Date().toISOString()
          })
          .eq('id', lead.id);
        
        if (updateError) throw updateError;
        
        toast({
          title: 'Lead converted!',
          description: `${fullLeadData.first_name} ${fullLeadData.last_name} was converted to contact.`,
        });
      } else {
        // Atualização normal de status
        const updateData = { 
          lifecycle_stage: toLifecycle(newStatus)
        };
        
        // Se for not_qualified, adiciona o motivo
        if (newStatus === 'not_qualified' && notConvertedReason) {
          updateData.not_converted_reason = notConvertedReason;
        }
        
        const { error } = await supabase
          .from('leads')
          .update(updateData)
          .eq('id', lead.id);
        if (error) throw error;
      }
      
      await loadLeads();
    } catch (e) {
      console.error('Failed to update lead status', e);
      toast({
        title: 'Erro',
        description: e?.message || 'Não foi possível atualizar o status do lead.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleImportTodayLeads = async () => {
    setIsImporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('facebook-leads-import');
      
      if (error) throw error;
      
      toast({
        title: "Import concluído",
        description: `${data.imported} leads importados de hoje com sucesso!`,
      });
      
      // Reload leads
      await loadLeads();
    } catch (e) {
      console.error('Import error:', e);
      toast({
        title: "Import error",
        description: e.message || "Error fetching Facebook leads",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleFixMarket = async () => {
    if (!confirm('Deseja corrigir o market de todos os leads? Esta operação pode levar alguns minutos.')) {
      return;
    }
    
    try {
      await fixMarket();
      // Reload leads after fix
      await loadLeads();
    } catch (e) {
      console.error('Fix market error:', e);
    }
  };

  // Selection functions
  const toggleSelectLead = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const clearSelection = () => {
    setSelectedLeadIds([]);
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedLeadIds.length === 0) return;
    
    try {
      // Map UI status to DB lifecycle_stage
      const STATUS_TO_LIFECYCLE = {
        new: 'new',
        contacted: 'contacted',
        qualifying: 'qualifying',
        qualified: 'qualified',
        not_qualified: 'not_qualified',
      };
      const toLifecycle = (status) => STATUS_TO_LIFECYCLE[status] ?? 'new';

      // Update all selected leads
      const { error } = await supabase
        .from('leads')
        .update({ lifecycle_stage: toLifecycle(newStatus) })
        .in('id', selectedLeadIds);
      
      if (error) throw error;

      // If moving to qualified, convert each one to contact
      if (newStatus === 'qualified') {
        for (const leadId of selectedLeadIds) {
          const { data: fullLeadData, error: fetchError } = await supabase
            .from('leads')
            .select('*')
            .eq('id', leadId)
            .single();
          
          if (fetchError) continue; // Skip on error
          
          // Create the contact
          const { data: newContact, error: contactError } = await supabase
            .from('contacts')
            .insert({
              first_name: fullLeadData.first_name,
              last_name: fullLeadData.last_name,
              preferred_name: fullLeadData.preferred_name,
              primary_email: fullLeadData.primary_email,
              primary_phone: fullLeadData.primary_phone,
              primary_phone_country_code: fullLeadData.primary_phone_country_code,
              gender: fullLeadData.gender,
              language: fullLeadData.language,
              date_of_birth: fullLeadData.date_of_birth,
              country_of_birth: fullLeadData.country_code,
              status: 'active',
            })
            .select()
            .single();
          
          if (contactError) continue; // Skip on error
          
          // Update lead with contact_id
          await supabase
            .from('leads')
            .update({ 
              contact_id: newContact.id,
              converted_at: new Date().toISOString()
            })
            .eq('id', leadId);
        }
      }

      toast({
        title: 'Sucesso!',
        description: `${selectedLeadIds.length} leads atualizados.`,
      });
      
      clearSelection();
      await loadLeads();
    } catch (e) {
      console.error('Erro ao atualizar leads em massa:', e);
      toast({
        title: 'Erro',
        description: e?.message || 'Não foi possível atualizar os leads.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFilter = (filterKey) => {
    if (filterKey === 'all') {
      setSearchQuery('');
      setAssignedFilter('all');
      setDateFilter('all');
      setKanbanColumnsFilter('all');
      setMarketFilter('all');
      toast.success("All filters removed");
      return;
    }
    
    switch (filterKey) {
      case 'searchQuery':
        setSearchQuery('');
        break;
      case 'assignedFilter':
        setAssignedFilter('all');
        break;
      case 'dateFilter':
        setDateFilter('all');
        break;
      case 'kanbanColumnsFilter':
        setKanbanColumnsFilter('all');
        break;
      case 'marketFilter':
        setMarketFilter('all');
        break;
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-x-hidden relative">
      {/* Falling Petals Animation - Exclusive for njunior@divuslegalgroup.com */}
      {!prefsLoading && shouldShowPetals('leads') && <FallingPetals />}
      
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 p-4 md:p-6 bg-slate-50 dark:bg-slate-900 overflow-hidden relative">
        {/* Header Flowers */}
        {!prefsLoading && shouldShowFlowers('leads') && <HeaderFlowers />}
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <ActiveFilters
                filters={{
                  searchQuery,
                  assignedFilter,
                  dateFilter,
                  marketFilter,
                  kanbanColumnsFilter
                }}
                onRemoveFilter={handleRemoveFilter}
                module="leads"
              />
            </div>

            <div className="flex gap-2 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(true)}
                className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-white hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
              </Button>

              <Button
                variant={assignedFilter === 'my_leads' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAssignedFilter(assignedFilter === 'my_leads' ? 'all' : 'my_leads')}
                className="flex items-center gap-2 h-9"
              >
                <UserIcon className="w-4 h-4" />
                My Leads
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleImportTodayLeads}
                disabled={isImporting}
                className="flex items-center gap-2 h-9"
              >
                <Download className="w-4 h-4" />
                {isImporting ? 'Importando...' : 'Importar Hoje'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleFixMarket}
                disabled={isFixingMarket}
                className="flex items-center gap-2 h-9"
                title="Corrige o market de todos os leads usando campanha ou telefone"
              >
                <RefreshCw className="w-4 h-4" />
                {isFixingMarket ? 'Corrigindo...' : 'Fix Market'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm('Deseja atribuir automaticamente todos os leads sem atribuição?')) {
                    bulkAssignLeads();
                  }
                }}
                disabled={isAssigningLeads}
                className="flex items-center gap-2 h-9"
                title="Atribui automaticamente todos os leads não atribuídos usando as regras de atribuição"
              >
                <UserCheck className="w-4 h-4" />
                {isAssigningLeads ? 'Atribuindo...' : 'Atribuir Pendentes'}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${
                  (assignedFilter !== "all" || dateFilter !== "all" || sortBy !== "created_date_desc" || kanbanColumnsFilter !== "all" || marketFilter !== "all") 
                    ? 'bg-primary text-white hover:bg-primary/90' 
                    : 'hover:bg-slate-200'
                }`}
              >
                <Filter className="w-4 h-4" />
              </Button>
              
              {viewMode === "table" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full h-9 w-9 p-0 flex-shrink-0 hover:bg-slate-200"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="end">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-sm text-slate-900">Show Columns</h4>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-name"
                            checked={visibleColumns.name}
                            onCheckedChange={() => handleToggleColumn('name')}
                          />
                          <Label htmlFor="col-name" className="text-sm cursor-pointer">
                            Name
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-contact"
                            checked={visibleColumns.contact}
                            onCheckedChange={() => handleToggleColumn('contact')}
                          />
                          <Label htmlFor="col-contact" className="text-sm cursor-pointer">
                            Contact Info
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-company"
                            checked={visibleColumns.company}
                            onCheckedChange={() => handleToggleColumn('company')}
                          />
                          <Label htmlFor="col-company" className="text-sm cursor-pointer">
                            Company
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-status"
                            checked={visibleColumns.status}
                            onCheckedChange={() => handleToggleColumn('status')}
                          />
                          <Label htmlFor="col-status" className="text-sm cursor-pointer">
                            Status
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-source"
                            checked={visibleColumns.source}
                            onCheckedChange={() => handleToggleColumn('source')}
                          />
                          <Label htmlFor="col-source" className="text-sm cursor-pointer">
                            Source
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-assigned"
                            checked={visibleColumns.assignedTo}
                            onCheckedChange={() => handleToggleColumn('assignedTo')}
                          />
                          <Label htmlFor="col-assigned" className="text-sm cursor-pointer">
                            Assigned To
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-created-by"
                            checked={visibleColumns.createdBy}
                            onCheckedChange={() => handleToggleColumn('createdBy')}
                          />
                          <Label htmlFor="col-created-by" className="text-sm cursor-pointer">
                            Created By
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="col-created-date"
                            checked={visibleColumns.createdDate}
                            onCheckedChange={() => handleToggleColumn('createdDate')}
                          />
                          <Label htmlFor="col-created-date" className="text-sm cursor-pointer">
                            Data de Criação
                          </Label>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              <ViewSwitcher 
                viewMode={viewMode === 'kanban' ? 'table' : viewMode} 
                onViewChange={(mode) => {
                  if (mode === 'table') setViewMode('kanban');
                  else setViewMode(mode);
                }}
              />
            </div>
          </div>

          {showFilters && (
            <div className="mb-4 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Assigned Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">Assigned To</Label>
                  <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All leads" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All leads</SelectItem>
                      <SelectItem value="my_leads">🔹 My Leads</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users
                        .sort((a, b) => {
                          const nameA = (a.display_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '').toLowerCase();
                          const nameB = (b.display_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '').toLowerCase();
                          return nameA.localeCompare(nameB);
                        })
                        .map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">Data de Criação</Label>
                  <Select value={dateFilter} onValueChange={setDateFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todas as datas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as datas</SelectItem>
                      <SelectItem value="today">Hoje</SelectItem>
                      <SelectItem value="yesterday">Ontem</SelectItem>
                      <SelectItem value="this_week">Esta Semana</SelectItem>
                      <SelectItem value="this_month">Este Mês</SelectItem>
                      <SelectItem value="custom">Selecionar Período</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Date Range Picker */}
                {dateFilter === "custom" && (
                  <div className="flex-1">
                    <Label className="text-xs text-slate-600 mb-2 block">Date Range</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateRange?.from ? (
                            customDateRange.to ? (
                              <>
                                {new Date(customDateRange.from).toLocaleDateString()} - {new Date(customDateRange.to).toLocaleDateString()}
                              </>
                            ) : (
                              new Date(customDateRange.from).toLocaleDateString()
                            )
                          ) : (
                            "Select date range"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={customDateRange}
                          onSelect={setCustomDateRange}
                          initialFocus
                          numberOfMonths={2}
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Sort By */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_date_desc">Newest First</SelectItem>
                      <SelectItem value="created_date_asc">Oldest First</SelectItem>
                      <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                      <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Kanban Columns Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">Columns</Label>
                  <Select value={kanbanColumnsFilter} onValueChange={setKanbanColumnsFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="ativas">Active</SelectItem>
                      <SelectItem value="nao_ativas">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Market Filter */}
                <div className="flex-1">
                  <Label className="text-xs text-slate-600 mb-2 block">Market</Label>
                  <Select value={marketFilter} onValueChange={setMarketFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Markets</SelectItem>
                      <SelectItem value="US">🇺🇸 US</SelectItem>
                      <SelectItem value="BR">🇧🇷 BR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters Button */}
                {(assignedFilter !== "all" || dateFilter !== "all" || sortBy !== "created_date_desc" || kanbanColumnsFilter !== "all" || marketFilter !== "all") && (
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAssignedFilter("all");
                        setDateFilter("all");
                        setCustomDateRange(null);
                        setSortBy("created_date_desc");
                        setKanbanColumnsFilter("all");
                        setMarketFilter("all");
                      }}
                      className="h-10"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-hidden">
        {viewMode === "kanban" ? (
          /* Kanban: Full width with horizontal scroll only */
          <div className="h-full overflow-x-auto overflow-y-auto md:overflow-y-hidden px-4 md:px-6 pb-4">
            <LeadKanban
              leads={filteredLeads}
              isLoading={isLoading}
              onViewLead={handleViewLead}
              onStatusChange={handleStatusChange}
              kanbanColumnsFilter={kanbanColumnsFilter}
              selectedLeadIds={selectedLeadIds}
              onToggleSelect={toggleSelectLead}
            />
          </div>
        ) : (
          /* Grid/Table: Contained with vertical scroll */
          <div className="h-full overflow-y-auto px-4 md:px-6 pb-4">
            <div className="max-w-7xl mx-auto">
              {viewMode === "grid" && (
                <LeadList
                  leads={filteredLeads}
                  isLoading={isLoading}
                  onViewLead={handleViewLead}
                  selectedLeadIds={selectedLeadIds}
                  onToggleSelect={toggleSelectLead}
                />
              )}

              {viewMode === "list" && (
                <LeadListView
                  leads={filteredLeads}
                  isLoading={isLoading}
                  onViewLead={handleViewLead}
                  selectedLeadIds={selectedLeadIds}
                  onToggleSelect={toggleSelectLead}
                />
              )}

              {viewMode === "table" && (
                <LeadTable
                  leads={filteredLeads}
                  isLoading={isLoading}
                  onViewLead={handleViewLead}
                  visibleColumns={visibleColumns}
                  users={users}
                  selectedLeadIds={selectedLeadIds}
                  onToggleSelect={toggleSelectLead}
                />
              )}

              {!isLoading && filteredLeads.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500 text-lg">No leads found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lead Form Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
            <DialogDescription>Fill in the lead data and click save.</DialogDescription>
          </DialogHeader>
          <LeadForm
            onSubmit={handleCreateLead}
            onCancel={() => setShowForm(false)}
            isProcessing={isProcessing}
            initialPhone={new URLSearchParams(window.location.search).get('phone')}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedLeadIds.length}
        onClearSelection={clearSelection}
        onAction={handleBulkStatusChange}
        itemType="leads"
        placeholder="Selecionar novo status..."
        options={[
          { value: 'new', label: 'New' },
          { value: 'contacted', label: 'Contacted' },
          { value: 'qualifying', label: 'Qualifying' },
          { value: 'qualified', label: 'Qualified' },
          { value: 'not_qualified', label: 'Not Qualified' },
        ]}
      />
    </div>
  );
}
